import React, { useCallback, useMemo, useState } from 'react';
import {
  Button,
  Col,
  DatePicker,
  Row,
  Table,
  notification,
} from 'antd';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { getDateRangeFromConfig } from '../../../../analytics/analytics.helpers';
import OnTraccrButton from '../../../../common/buttons/OnTraccrButton';
import OnTraccrButtonDropdown from '../../../../common/buttons/OnTraccrButtonDropdown';
import FullScreenIcon from '../../../../common/icons/files/FullScreenIcon';
import OntraccrTag from '../../../../common/OntraccrTag';
import TagRender from '../../../../common/TagRender';
import Colors from '../../../../constants/Colors';
import DateFilter from '../../../../datePicker/DateFilter';
import { getFormattedCurrency } from '../../../../forms/formHelpers';
import { getProgressSummaryColumns } from '../../../ProjectProgress/ProgressColumns';
import {
  prepareExportData,
  exportFile,
  SUMMARY_EXPORT_TYPE,
  LABOR_EXPORT_TYPE,
  MATERIAL_EXPORT_TYPE,
  OVERHEAD_EXPORT_TYPE,
  PHASED_EXPORT_TYPE,
} from '../../../ProjectProgress/export/exportHelpers';
import { updateProjectProgressFilters } from '../../../state/projects.actions';
import BudgetTrackingCollapse from '../BudgetTrackingCollapse';
import CostToDateAnalysisModal from './CostToDateAnalysisModal';
import CostToDateDetailModal from './CostToDateDetailModal';
import { UNSUPPORTED_DETAIL_KEYS } from '../../budgetTrackingHelpers';

const ACTUAL_TO_ESTIMATED_KEY = {
  actualTotalSpend: 'estimatedTotalCost',
  actualMaterialSpend: 'estimatedMaterialCost',
  actualOverheadSpend: 'estimatedOverheadCost',
  actualLaborSpend: 'estimatedLaborCost',
  actualEquipmentSpend: 'estimatedEquipmentCost',
  actualBurdenCost: 'estimatedBurdenCost',
  actualSubcontractSpend: 'estimatedSubcontractCost',
};

const MENU_ITEMS = [
  { key: 'excel', label: 'Export to Excel' },
  { key: 'pdf', label: 'Export to PDF' },
];

const SUMMARY_KEY_TO_CATEGORY_NAME = {
  actualMaterialSpend: 'Material',
  actualOverheadSpend: 'Overhead',
  actualLaborSpend: 'Labor',
  actualEquipmentSpend: 'Equipment',
};

const getCategoryFilterForKey = (key) => {
  if (key == null || key === 'actualTotalSpend') return null;
  if (key.startsWith('custom-')) return key.replace('custom-', '');
  return SUMMARY_KEY_TO_CATEGORY_NAME[key] ?? null;
};

/** Returns rows matching the category filter (each costcode has a category at most once). */
const getRowsByCategory = (rows, categoryFilter) => {
  if (!categoryFilter || !rows?.length) return [];
  // Default categories: filter is name ('Material', 'Labor') → match categoryName.
  // Custom categories: filter is id (from 'custom-{id}') → match categoryId.
  const matches = (name, id) => (
    name === categoryFilter || String(id) === categoryFilter
  );
  return (rows || []).flatMap((r) => {
    const fromChildren = getRowsByCategory(r.children || [], categoryFilter);
    return matches(r.categoryName, r.categoryId) ? [r, ...fromChildren] : fromChildren;
  });
};

const hideNameCodeForChild = (isTreeView) => (val, record) => (
  record.isChildRow && isTreeView ? '' : (val ?? '—')
);

export default function CostTrackingAnalysis({
  onUploadInvoice,
  userMap,
  projectId,
  summaryData,
  costcodesTableData,
  hasUncategorizedInvoices,
  averageWage,
}) {
  const dispatch = useDispatch();

  const { datePreset, dateRange } = useSelector((state) => state.projects.projectProgressFilters);
  const categoriesIdMap = useSelector((state) => state.costcodes.categoriesIdMap);
  const progresses = useSelector((state) => state.reports?.progress) || {};
  const projectCostcodeDetailsMap = useSelector((state) => state.projects?.projectCostcodeDetails);
  const projectProgressFilters = useSelector((state) => state.projects?.projectProgressFilters);
  const companyImageURL = useSelector((state) => state.settings?.company?.companyImageURL);

  const [open, setOpen] = useState(false);
  const [fullscreenModalOpen, setFullscreenModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailModalSelectedKey, setDetailModalSelectedKey] = useState(null);

  const onDateRangeChange = useCallback((newDateRange) => {
    let newDateRangeFilter;

    if (newDateRange) {
      const [startDate, endDate] = newDateRange;
      newDateRangeFilter = {
        start: startDate.startOf('day').unix() * 1000,
        end: endDate.endOf('day').unix() * 1000,
      };

      notification.warn({
        message: 'Date range filter selected',
        description: 'Please note that values on the progress page may not be updated while date range filter is turned on.',
      });
    }
    dispatch(updateProjectProgressFilters({
      dateRange: newDateRangeFilter,
      datePreset: undefined,
    }));
  }, []);

  const onPresetClicked = useCallback((newDatePreset) => {
    notification.warn({
      message: 'Date range filter selected',
      description: 'Please note that values on the progress page may not be updated while date range filter is turned on.',
    });
    dispatch(updateProjectProgressFilters({
      dateRange: undefined,
      datePreset: newDatePreset,
    }));
    setOpen(false);
  }, []);

  const {
    categoriesLength,
    customCategories,
  } = useMemo(() => {
    const categoriesArray = Object.values(categoriesIdMap);
    return {
      categoriesLength: categoriesArray.length,
      customCategories: categoriesArray.filter((category) => !category.isDefault),
    };
  }, [categoriesIdMap]);

  const formattedRange = useMemo(() => {
    if (dateRange) {
      const rangeArray = [dateRange.start, dateRange.end];
      return getDateRangeFromConfig({ dateRange: rangeArray, datePreset });
    }
    return getDateRangeFromConfig({ dateRange, datePreset });
  }, [datePreset, dateRange]);

  const summaryColumns = useMemo(() => (
    getProgressSummaryColumns(hasUncategorizedInvoices, customCategories)
  ), [hasUncategorizedInvoices, customCategories]);

  const summaryCardsData = useMemo(() => {
    if ((!detailModalOpen && !fullscreenModalOpen) || !summaryData?.length) return [];
    const record = summaryData?.[0];
    if (!record) return [];
    return summaryColumns.map((col) => {
      const actualKey = col.dataIndex;
      const actual = Number(record[actualKey]) || 0;
      let estimated = null;
      if (col.key?.startsWith('custom-')) {
        estimated = Number(record[`estimated_${actualKey}`]) || 0;
      } else if (col.key !== 'uncategorizedCosts') {
        const estKey = ACTUAL_TO_ESTIMATED_KEY[col.key];
        estimated = estKey ? (Number(record[estKey]) || 0) : null;
      }
      const estimatedNum = estimated ?? 0;
      const hasEstimated = estimated != null;
      const maxVal = Math.max(estimatedNum, actual, 1);
      const estPct = maxVal ? (estimatedNum / maxVal) * 100 : 0;
      const actPct = maxVal ? (actual / maxVal) * 100 : 0;
      const difference = hasEstimated ? estimatedNum - actual : null;
      const isFavorable = difference != null && difference >= 0;
      return {
        key: col.key,
        title: col.title,
        estimated: estimatedNum,
        actual,
        hasEstimated,
        estPct,
        actPct,
        difference,
        isFavorable,
      };
    });
  }, [summaryData, summaryColumns, detailModalOpen, fullscreenModalOpen]);

  const detailModalOptions = useMemo(() => (
    summaryColumns
      .filter((col) => !UNSUPPORTED_DETAIL_KEYS.has(col.key))
      .map((col) => ({ label: col.title, value: col.key }))
  ), [summaryColumns]);

  const detailModalSelectedCard = useMemo(() => (
    summaryCardsData.find((c) => c.key === detailModalSelectedKey) ?? null
  ), [summaryCardsData, detailModalSelectedKey]);

  const detailTableData = useMemo(() => {
    if (!costcodesTableData?.length || !detailModalSelectedKey) return [];
    const categoryFilter = getCategoryFilterForKey(detailModalSelectedKey);
    if (detailModalSelectedKey === 'actualTotalSpend' || categoryFilter === null) {
      return costcodesTableData;
    }

    return getRowsByCategory(costcodesTableData, categoryFilter);
  }, [costcodesTableData, detailModalSelectedKey]);

  const detailTableColumns = useMemo(() => {
    const isTreeView = !detailModalSelectedKey
      || detailModalSelectedKey === 'actualTotalSpend'
      || getCategoryFilterForKey(detailModalSelectedKey) === null;
    return [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        render: hideNameCodeForChild(isTreeView),
      },
      {
        title: 'Code',
        dataIndex: 'code',
        key: 'code',
        render: hideNameCodeForChild(isTreeView),
      },
      {
        title: 'Category',
        dataIndex: 'categoryId',
        key: 'category',
        render: (_, record) => {
          const { categoryId, isParentRow } = record;
          if (isParentRow) {
            return (
              <OntraccrTag style={{ fontSize: 12, fontFamily: 'open-sans-600', color: Colors.GRAY_950 }}>
                Multi
              </OntraccrTag>
            );
          }
          if (!categoryId) return '—';
          return (
            <TagRender
              tagMap={categoriesIdMap}
              tagIds={[categoryId]}
              useRedesign
            />
          );
        },
      },
      {
        title: 'Phase',
        dataIndex: 'phaseName',
        key: 'phase',
        render: (v) => v ?? '—',
      },
      {
        title: 'Total Cost',
        dataIndex: 'totalCost',
        key: 'totalCost',
        render: (v) => getFormattedCurrency(v),
      },
      {
        title: 'Estimated Cost',
        key: 'estimatedCost',
        render: (_, record) => getFormattedCurrency(record.categoryAssignment?.estimatedCost ?? 0),
      },
      {
        title: 'Remaining Budget',
        key: 'remainingBudget',
        render: (_, record) => {
          const estimatedCost = record.categoryAssignment?.estimatedCost ?? 0;
          return getFormattedCurrency(estimatedCost - (record.totalCost ?? 0));
        },
      },
    ];
  }, [categoriesIdMap, detailModalSelectedKey]);

  const summaryColumnsWithCellClick = useMemo(() => (
    summaryColumns.map((col) => {
      if (UNSUPPORTED_DETAIL_KEYS.has(col.key)) return col;
      return {
        ...col,
        onCell: () => ({
          className: 'cost-tracking-analysis-table-cell-clickable',
          onClick: () => {
            setFullscreenModalOpen(false);
            setDetailModalSelectedKey(col.key);
            setDetailModalOpen(true);
          },
        }),
      };
    })
  ), [summaryColumns]);

  const openDetailModal = useCallback((columnKey) => {
    if (UNSUPPORTED_DETAIL_KEYS.has(columnKey)) return;
    setFullscreenModalOpen(false);
    setDetailModalSelectedKey(columnKey);
    setDetailModalOpen(true);
  }, []);

  const onFullscreenClick = useCallback((e) => {
    e.stopPropagation();
    setFullscreenModalOpen(true);
  }, []);

  const onExportClick = useCallback((itemInfo) => {
    const { key } = itemInfo;
    const isPDF = key === 'pdf';
    if (!projectId) return;
    const flatRows = (costcodesTableData || []).flatMap((row) => (
      row.isParentRow && row.children?.length ? row.children : [row]
    )).map((r) => ({ ...r, projectId, costcodeId: r.id }));
    const unphasedCCs = flatRows.filter((r) => !r.phaseId && r.categoryName === 'Labor');
    const materialCodes = flatRows.filter((r) => !r.phaseId && r.categoryName === 'Material');
    const overheadCodes = flatRows.filter((r) => !r.phaseId && r.categoryName === 'Overhead');
    const phaseMap = {};
    flatRows.filter((r) => r.phaseId && r.categoryName === 'Labor').forEach((r) => {
      if (!phaseMap[r.phaseId]) {
        phaseMap[r.phaseId] = { id: r.phaseId, name: r.phaseName || r.phaseId, children: [] };
      }
      phaseMap[r.phaseId].children.push(r);
    });
    const phasedCodes = Object.values(phaseMap);
    const preparedSummaryData = prepareExportData({
      data: summaryData,
      type: SUMMARY_EXPORT_TYPE,
    });
    const preparedLaborData = prepareExportData({
      data: unphasedCCs,
      type: LABOR_EXPORT_TYPE,
      progressMap: progresses,
      costcodeDetailsMap: projectCostcodeDetailsMap,
      userMap,
      averageWage,
      projectId,
    });
    const preparedMaterialData = prepareExportData({
      data: materialCodes,
      type: MATERIAL_EXPORT_TYPE,
      progressMap: progresses,
      costcodeDetailsMap: projectCostcodeDetailsMap,
    });
    const preparedOverheadData = prepareExportData({
      data: overheadCodes,
      type: OVERHEAD_EXPORT_TYPE,
      progressMap: progresses,
      costcodeDetailsMap: projectCostcodeDetailsMap,
    });
    const preparedPhasedData = prepareExportData({
      data: phasedCodes,
      type: PHASED_EXPORT_TYPE,
      progressMap: progresses,
      costcodeDetailsMap: projectCostcodeDetailsMap,
    });
    exportFile({
      isPDF,
      companyImageURL,
      summaryData: preparedSummaryData,
      laborData: preparedLaborData,
      materialData: preparedMaterialData,
      overheadData: preparedOverheadData,
      phasedData: preparedPhasedData,
      title: 'Project Progress',
      dateRange: projectProgressFilters?.dateRange,
    });
  }, [
    summaryData,
    costcodesTableData,
    projectId,
    progresses,
    projectCostcodeDetailsMap,
    projectProgressFilters,
    userMap,
    companyImageURL,
  ]);

  return (
    <BudgetTrackingCollapse
      panelKey="analysis"
      header={(
        <Row justify="space-between" style={{ marginBottom: 24, width: '100%' }} align="middle">
          <Col>
            <span className="budget-tracking-section-title">Cost to Date Analysis</span>
            <OntraccrTag
              style={{
                marginLeft: 14,
                fontFamily: 'open-sans-600',
                color: Colors.GRAY_950,
              }}
            >
              {categoriesLength} categories
            </OntraccrTag>
          </Col>
          <Col flex="none" className="cost-tracking-analysis-header-actions">
            <Button
              type="default"
              icon={<FullScreenIcon width={15} height={15} />}
              className="cost-tracking-analysis-fullscreen-btn"
              onClick={onFullscreenClick}
            />
            <OnTraccrButtonDropdown
              buttonTitle="Export"
              menuItems={MENU_ITEMS}
              onMenuClick={onExportClick}
            />
          </Col>
        </Row>
      )}
    >
      <Row justify="space-between" style={{ marginBottom: 16 }}>
        <Col>
          <Row>
            <Col>
              <DatePicker.RangePicker
                format="MMM Do YY"
                className="form-field-datetime-container"
                onChange={onDateRangeChange}
                value={formattedRange}
                open={open}
                onOpenChange={setOpen}
                renderExtraFooter={() => (
                  <DateFilter datePreset={datePreset} handlePreset={onPresetClicked} />
                )}
              />
            </Col>
            {onUploadInvoice && (
              <Col>
                <OnTraccrButton
                  title="Upload Invoice"
                  onClick={onUploadInvoice}
                  style={{ marginLeft: 10, height: 40 }}
                />
              </Col>
            )}
          </Row>
        </Col>
        <Col>
          <div className="project-progress-summary-legend">
            <div
              className="project-progress-summary-legend-item"
              style={{
                backgroundColor: 'rgb(219, 68, 55)',
              }}
            />
            Actual
          </div>
          <div className="project-progress-summary-legend">
            <div
              className="project-progress-summary-legend-item"
              style={{
                backgroundColor: 'rgb(66, 133, 244)',
              }}
            />
            Estimated
          </div>
          {hasUncategorizedInvoices && (
            <div className="project-progress-summary-legend">
              <div
                className="project-progress-summary-legend-item"
                style={{
                  backgroundColor: 'rgb(0, 128, 0)',
                }}
              />
              Uncategorized
            </div>
          )}
        </Col>
      </Row>
      <Table
        className="cost-tracking-analysis-table budget-tracking-table"
        rowClassName="file-table-row"
        columns={summaryColumnsWithCellClick}
        dataSource={summaryData}
        pagination={false}
        expandable={false}
        scroll={{ x: 'max-content' }}
      />
      <CostToDateAnalysisModal
        visible={fullscreenModalOpen}
        onCancel={() => setFullscreenModalOpen(false)}
        cards={summaryCardsData}
        onCardClick={openDetailModal}
      />
      <CostToDateDetailModal
        visible={detailModalOpen}
        onCancel={() => setDetailModalOpen(false)}
        selectedKey={detailModalSelectedKey}
        onSelectedKeyChange={setDetailModalSelectedKey}
        options={detailModalOptions}
        selectedCard={detailModalSelectedCard}
        tableColumns={detailTableColumns}
        tableData={detailTableData}
      />
    </BudgetTrackingCollapse>
  );
}

/* eslint-disable react/forbid-prop-types */
CostTrackingAnalysis.propTypes = {
  onUploadInvoice: PropTypes.func,
  projectId: PropTypes.string,
  summaryData: PropTypes.array,
  costcodesTableData: PropTypes.array,
  hasUncategorizedInvoices: PropTypes.bool,
  userMap: PropTypes.shape({}),
  averageWage: PropTypes.number,
};

CostTrackingAnalysis.defaultProps = {
  onUploadInvoice: null,
  projectId: null,
  summaryData: [],
  costcodesTableData: [],
  hasUncategorizedInvoices: false,
  userMap: {},
  averageWage: 0,
};
